import type { SpinRequest } from "@slot/client-engine";
import type { BalanceStore } from "../balance/BalanceStore";
import type { BetSystem } from "../bet/BetSystemTypes";
import type { FeatureModeStore } from "../feature-mode/FeatureModeStore";
import { multiplyMinorByMultiplier } from "../money/multiplyMinorByMultiplier";
import type { Log } from "@slot/presentation";

export interface SpinRequestBuilderDeps {
  betSystem: BetSystem;
  balanceStore: BalanceStore;
  featureModeStore: FeatureModeStore;
  /** ISO currency code the spin requests carry. */
  currencyCode: string;
  /** Operator settings: feature purchase / feature mode bet multipliers. */
  featurePriceMultipliersById: Readonly<Record<string, number>>;
  featureModeMultipliersById: Readonly<Record<string, number>>;
  log: Log;
}

/**
 * The spin request for the current bet: a feature buy names the feature, else the active feature
 * mode rides along. The round's cost is deducted from the balance optimistically (the server's
 * snapshot overwrites it on the response). A title whose bet covers lines (decision 56) sends their
 * count as `requestCustomPayload.betLines`, the bet being their total; any other title's request has
 * no such key at all — the platform's request schema is strict.
 */
export class SpinRequestBuilder {
  public constructor(private readonly deps: SpinRequestBuilderDeps) {}

  public build(input: { requestedFeatureIdentifier?: string } = {}): SpinRequest {
    const betMinor = this.deps.betSystem.getCurrentBetMinor();
    const requestedFeatureIdentifier = input.requestedFeatureIdentifier;
    const requestedFeatureModeIdentifier = requestedFeatureIdentifier ? undefined : this.deps.featureModeStore.getSnapshot().activeMode?.id;
    const costMinor = this.roundCostMinor(betMinor, requestedFeatureIdentifier, requestedFeatureModeIdentifier);
    this.deps.balanceStore.deductMinorBetFromBalance(costMinor);
    this.deps.log(`[SpinRequest] bet=${betMinor} cost=${costMinor} feature=${requestedFeatureIdentifier ?? "none"} featureMode=${requestedFeatureModeIdentifier ?? "none"}`);
    return {
      currencyCode: this.deps.currencyCode,
      betAmount: betMinor,
      playerIdentifier: "game-runtime-player",
      requestedFeatureIdentifier,
      requestedFeatureModeIdentifier,
      ...(this.deps.betSystem.getLineOptions().length > 0 ? { requestCustomPayload: { betLines: this.deps.betSystem.getLines() } } : {}),
    };
  }

  /** A feature buy costs the bet times the feature's price; a feature mode the bet times its multiplier; a plain spin the bet. */
  public roundCostMinor(betMinor: string, requestedFeatureIdentifier?: string, requestedFeatureModeIdentifier?: string): string {
    if (requestedFeatureIdentifier) {
      return multiplyMinorByMultiplier(betMinor, this.multiplierOrThrow(this.deps.featurePriceMultipliersById, requestedFeatureIdentifier, "feature"));
    }
    if (requestedFeatureModeIdentifier) {
      return multiplyMinorByMultiplier(betMinor, this.multiplierOrThrow(this.deps.featureModeMultipliersById, requestedFeatureModeIdentifier, "feature mode"));
    }
    return betMinor;
  }

  private multiplierOrThrow(multipliersById: Readonly<Record<string, number>>, id: string, what: string): number {
    const multiplier = multipliersById[id];
    if (typeof multiplier !== "number" || !Number.isFinite(multiplier) || multiplier <= 0) {
      throw new Error(`[SpinRequest] missing or invalid ${what} multiplier for "${id}"`);
    }
    return multiplier;
  }
}
